import type { EquippedItem } from '@moussenaires/shared';
import { ExternalLink, X } from 'lucide-react';
import { useEffect, useRef, type CSSProperties } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import { mediaSrc } from '../api/client.js';
import { useClickOutside } from '../hooks/useClickOutside.js';
import './ItemPopover.css';

const WIDTH = 280;
const MARGIN = 8;

/** Pins the popover under (or, if there's no room, above) whatever gear icon triggered it, clamped to stay on-screen. */
function popoverStyle(anchorRect: DOMRect): CSSProperties {
  const left = Math.max(
    MARGIN,
    Math.min(
      anchorRect.left + anchorRect.width / 2 - WIDTH / 2,
      window.innerWidth - WIDTH - MARGIN,
    ),
  );
  const spaceBelow = window.innerHeight - anchorRect.bottom;
  const opensUpward = spaceBelow < 260 && anchorRect.top > spaceBelow;
  return opensUpward
    ? { left, bottom: window.innerHeight - anchorRect.top + MARGIN }
    : { left, top: anchorRect.bottom + MARGIN };
}

/**
 * The tooltip Armory would show for one piece of gear, anchored right under
 * the icon that was clicked — a popover, not a modal, so the rest of the
 * roster stays visible and interactive around it. Built entirely from what
 * the equipment sync already captured (no extra API call): stats, armor,
 * gems, set bonuses, the enchant, and a trinket's (or ring's/weapon's)
 * "Equip:"/"Use:" proc text. Wowhead's own page covers anything that isn't:
 * flavor text, drop source, etc.
 */
export function ItemPopover({
  item,
  anchorRect,
  onClose,
  onOutsideDismiss,
}: {
  item: EquippedItem;
  anchorRect: DOMRect;
  onClose: () => void;
  /**
   * Fired only when this popover is dismissed by a click landing outside
   * it — never for the explicit close button or Escape. The popover is
   * pinned right next to a gear icon near the edge of its hero card, so
   * that dismiss click very often physically lands on the *next* card;
   * callers use this to stop that same click from also toggling that
   * neighbor's focus.
   */
  onOutsideDismiss?: () => void;
}) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => {
    onOutsideDismiss?.();
    onClose();
  });

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return createPortal(
    <div
      ref={ref}
      className="item-popover"
      style={{ width: WIDTH, ...popoverStyle(anchorRect) }}
      role="dialog"
      aria-label={item.name}
    >
      <button
        type="button"
        className="item-popover-close"
        onClick={onClose}
        aria-label={t('common.close')}
      >
        <X size={14} />
      </button>

      <div className="item-popover-header">
        {item.iconUrl ? (
          <img
            src={mediaSrc(item.iconUrl)}
            alt=""
            className="item-popover-icon"
            style={{ boxShadow: `0 0 0 2px ${item.qualityColor ?? '#666'}` }}
          />
        ) : (
          <div className="item-popover-icon item-popover-icon-placeholder" />
        )}
        <div>
          <p className="item-popover-name" style={{ color: item.qualityColor ?? undefined }}>
            {item.name}
          </p>
          <p className="item-popover-slot">
            {item.slotLabel}
            {item.itemLevel ? ` · ${t('roster.itemLevel')} ${item.itemLevel}` : ''}
          </p>
        </div>
      </div>

      {item.armor && <p className="item-popover-armor">{item.armor}</p>}

      {(item.stats?.length ?? 0) > 0 && (
        <ul className="item-popover-stats">
          {item.stats.map((stat) => (
            <li key={stat}>{stat}</li>
          ))}
        </ul>
      )}

      {(item.spellDescriptions?.length ?? 0) > 0 && (
        <ul className="item-popover-spells">
          {item.spellDescriptions.map((description) => (
            <li key={description}>{description}</li>
          ))}
        </ul>
      )}

      {(item.enchant?.length ?? 0) > 0 && (
        <p className="item-popover-enchant">{item.enchant.join(', ')}</p>
      )}

      {(item.gems?.length ?? 0) > 0 && (
        <ul className="item-popover-gems">
          {item.gems.map((gem) => (
            <li key={gem.name}>
              {gem.iconUrl ? (
                <img src={mediaSrc(gem.iconUrl)} alt="" className="item-popover-gem-icon" />
              ) : (
                <span className="item-popover-gem-icon item-popover-gem-icon-placeholder" />
              )}
              <span>
                <span className="item-popover-gem-name">{gem.name}</span>
                {gem.bonus && <span className="item-popover-gem-bonus">{gem.bonus}</span>}
              </span>
            </li>
          ))}
        </ul>
      )}

      {item.setName && (
        <div className="item-popover-set">
          <p className="item-popover-set-name">{item.setName}</p>
          {(item.setBonuses ?? []).map((bonus) => (
            <p key={bonus} className="item-popover-set-bonus">
              {bonus}
            </p>
          ))}
        </div>
      )}

      {item.itemId && (
        <a
          href={`https://www.wowhead.com/item=${item.itemId}`}
          target="_blank"
          rel="noreferrer"
          className="item-popover-link"
        >
          <ExternalLink size={12} /> {t('roster.viewOnWowhead')}
        </a>
      )}
    </div>,
    document.body,
  );
}
